import { StandardProps } from "@/types/types";
import styles from "./Facets.module.scss";
import classNames from "classnames/bind";
import { FacetGroup } from "./components/FacetGroup/FacetGroup";
import { FacetItem } from "./components/FacetItem/FacetItem";
import { useState } from "react";
import { getFacetsWithSelectedValues } from "@/utils/setJLData";
import { useRouteParams } from "@/hooks/useRouteParams";
import { sendMonetateEvent } from "@/utils/sendMonetateEvent";
import { createDispatchFacetAnalytics } from "./utils/createDispatchFacetAnalytics";

const cx = classNames.bind(styles);

const SINGLE_ITEM_DIMENSIONS = [
  "childSKUs.hideOutOfStock",
  "product.newIn",
  "exclusiveToJl",
];

const DEPARTMENT_DIMENSION = "Store.Department";

const NUMBER_OF_OPEN_FACET_ACCORDIONS = 3;

export function Facets({
  productListingData,
  configProps,
  targetFacetName,
}: Pick<StandardProps, "productListingData" | "configProps"> & {
  targetFacetName?: string;
}) {
  const route = useRouteParams();

  const { facets } = productListingData;

  const singleItemFacets = facets.filter((facet) =>
    SINGLE_ITEM_DIMENSIONS.includes(facet.dimensionName),
  );

  const nonSingleItemFacets = facets.filter(
    (facet) => !SINGLE_ITEM_DIMENSIONS.includes(facet.dimensionName),
  );

  const dispatchFacetAnalytics = createDispatchFacetAnalytics({
    pageType: route.pageType,
    results: productListingData.results,
    getSelectedFacets: () =>
      getFacetsWithSelectedValues(productListingData.facets),
  });

  return (
    <ul
      className={cx("facets", {
        "facets--desktopOnly": configProps?.desktopOnly,
        "facets--drawer": configProps?.isDrawer,
        "facets--withPadding": configProps?.filterDrawerPadding,
      })}
      data-component="Facets"
      data-testid="facets"
      onClick={() => {
        sendMonetateEvent("plp.facets.interaction");
      }}
    >
      {singleItemFacets.map(
        (facet) =>
          facet.details[0] && (
            <li
              key={facet.dimensionName}
              className={cx("facet", "facet--singleItem")}
              data-testid="facet-single-item"
            >
              <FacetItem
                dimensionName={facet.dimensionName}
                facetName={facet.name}
                facetDetails={facet.details[0]}
                dispatchFacetAnalytics={dispatchFacetAnalytics}
              />
            </li>
          ),
      )}
      {nonSingleItemFacets
        .sort((a, b) => {
          if (a.dimensionName === DEPARTMENT_DIMENSION) return -1;
          if (b.dimensionName === DEPARTMENT_DIMENSION) return 1;
          return 0;
        })
        .map((facet, ind) => {
          const isTargetFacet =
            targetFacetName && facet.name === targetFacetName;

          return (
            <li
              key={facet.dimensionName}
              className={styles.facet}
              data-testid="facet-group"
            >
              <FacetGroup
                dispatchFacetAnalytics={dispatchFacetAnalytics}
                facet={{
                  ...facet,
                  name:
                    facet.dimensionName === DEPARTMENT_DIMENSION
                      ? "Select a Department"
                      : facet.name,
                }}
                open={
                  targetFacetName
                    ? !!isTargetFacet
                    : ind < NUMBER_OF_OPEN_FACET_ACCORDIONS
                }
                position={ind + 1}
              />
            </li>
          );
        })}
    </ul>
  );
}
